import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, Lock, ShieldCheck, Check, Sparkles } from 'lucide-react';
import { tokenizeCardPagarme } from '@/lib/pagarmeTokenize';

// FASE 11.2 — Endpoint OFICIAL de tokenização do Pagar.me v5 (client-side, chave pública).
// O número do cartão/CVV/validade permanecem EXCLUSIVAMENTE no navegador; ao backend só vai o token.
const PAGARME_PUBLIC_KEY = process.env.REACT_APP_PAGARME_PUBLIC_KEY;

function onlyDigits(v) {
  return (v || '').replace(/\D/g, '');
}

// Máscara: Amex (inicia 34/37) até 15 dígitos (4-6-5); demais bandeiras até 16 (grupos de 4).
// Impede digitar acima do tamanho permitido pela bandeira. Aceita SOMENTE dígitos.
function formatCardNumber(v) {
  let d = onlyDigits(v);
  const isAmex = /^3[47]/.test(d);
  d = d.slice(0, isAmex ? 15 : 16);
  if (isAmex) {
    const a = d.slice(0, 4);
    const b = d.slice(4, 10);
    const c = d.slice(10, 15);
    return [a, b, c].filter(Boolean).join(' ');
  }
  return d.replace(/(.{4})/g, '$1 ').trim();
}

// Máscara MM/AA — SOMENTE dígitos.
function formatExp(v) {
  const d = onlyDigits(v).slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

// Detecção de bandeira pelos dígitos iniciais.
function detectBrand(digits) {
  if (/^4/.test(digits)) return 'Visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'Mastercard';
  if (/^3[47]/.test(digits)) return 'Amex';
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011|506699)/.test(digits)) return 'Elo';
  if (/^(606282|3841)/.test(digits)) return 'Hipercard';
  if (/^(30|36|38)/.test(digits)) return 'Diners';
  return '';
}

// Validação Luhn do número do cartão.
function luhnValid(digits) {
  if (digits.length < 13) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

// Validade não expirada e mês 1..12.
function expValid(exp) {
  const [mm, yy] = (exp || '').split('/');
  if (!mm || !yy || mm.length !== 2 || yy.length !== 2) return false;
  const m = parseInt(mm, 10);
  const y = 2000 + parseInt(yy, 10);
  if (m < 1 || m > 12) return false;
  const now = new Date();
  const last = new Date(y, m, 0, 23, 59, 59);
  return last >= now;
}

// Formatação de moeda (apenas apresentação — não altera nenhuma regra de negócio).
function formatBRL(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return `R$ ${n.toFixed(2).replace('.', ',')}`;
}

const ACCEPTED_BRANDS = ['Visa', 'Mastercard', 'Elo', 'Hipercard'];

export default function ClientPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [preparing, setPreparing] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [card, setCard] = useState({ number: '', holder_name: '', exp: '', cvv: '' });

  const planData = location.state;
  // FASE 11.2K-III.1 — modo UPGRADE Pagar.me (troca de plano): reutiliza esta tela de
  // pagamento/tokenização (UX-01) para cobrar a DIFERENÇA CHEIA. NÃO cria assinatura nova.
  const isUpgradePagarme = !!planData?.upgrade_pagarme;

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    if (!planData) {
      navigate('/client/plans');
      return;
    }
    if (isUpgradePagarme) {
      setPreparing(false);
      return;
    }
    createPendingSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, planData]);

  const createPendingSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/client/subscribe`,
        {
          vehicle_id: planData.vehicle_id,
          plan_id: planData.plan_id,
          accepted_terms_version: planData.accepted_terms_version,
          accepted_refund_policy_version: planData.accepted_refund_policy_version,
          accepted_checkout_at: planData.accepted_checkout_at,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscriptionId(res.data.subscription_id);
      setPreparing(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Erro de autorização. Por favor, tente novamente.');
        navigate('/client/onboarding/plan');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao preparar o pagamento');
        navigate('/client/plans');
      }
    }
  };

  // Tokeniza o cartão DIRETO no navegador (Pagar.me.js / endpoint /tokens com chave pública).
  const tokenizeCard = async () => {
    return tokenizeCardPagarme(PAGARME_PUBLIC_KEY, card);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!luhnValid(onlyDigits(card.number))) {
      toast.error('Número do cartão inválido.');
      return;
    }
    if (!card.holder_name.trim() || !expValid(card.exp) || onlyDigits(card.cvv).length < 3) {
      toast.error('Preencha nome, validade (MM/AA) e CVV corretamente.');
      return;
    }
    setSubmitting(true);
    try {
      const cardToken = await tokenizeCard();
      const token = localStorage.getItem('token');

      // FASE 11.2K-III.1 / 11.2L — modo UPGRADE Pagar.me: cobra a DIFERENÇA CHEIA no cartão
      // tokenizado e conclui a alteração. A troca (plano OU veículo) só é aplicada APÓS a
      // cobrança ser aprovada (atomicidade no backend). Idempotência preservada.
      if (isUpgradePagarme) {
        const endpoint = planData.vehicle_change
          ? `${API}/client/vehicle/change-pagarme`
          : `${API}/client/plan/change-pagarme`;
        const payload = planData.vehicle_change
          ? {
              new_vehicle_id: planData.new_vehicle_id,
              card_token: cardToken,
              idempotency_key: planData.idempotency_key,
            }
          : {
              target_plan_id: planData.target_plan_id,
              card_token: cardToken,
              idempotency_key: planData.idempotency_key,
            };
        const res = await axios.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(res.data?.message || (planData.vehicle_change ? 'Veículo alterado com sucesso!' : 'Upgrade concluído com sucesso!'));
        navigate('/client/dashboard');
        return;
      }

      const res = await axios.post(
        `${API}/payments/checkout/session`,
        { subscription_id: subscriptionId, card_token: cardToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sessionStorage.removeItem('identified_vehicle');
      sessionStorage.removeItem('available_plans');
      sessionStorage.removeItem('vehicle_type_display');
      if (res.data?.mode === 'pagarme_subscription') {
        toast.success('Assinatura criada! Estamos confirmando o pagamento.');
        navigate('/client/payment-success', { state: { pagarme: true, subscription_id: subscriptionId } });
      } else {
        toast.error('Resposta inesperada do gateway.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Erro ao processar pagamento';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const cardDigits = onlyDigits(card.number);
  const brand = detectBrand(cardDigits);
  const formValid =
    luhnValid(cardDigits) &&
    !!card.holder_name.trim() &&
    expValid(card.exp) &&
    onlyDigits(card.cvv).length >= 3;

  if (preparing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200/80 shadow-xl p-10 text-center" data-testid="payment-preparing">
          <div className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="w-7 h-7 animate-spin text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">Preparando pagamento</h2>
          <p className="text-sm text-slate-500">Aguarde um instante enquanto tudo é preparado com segurança…</p>
        </div>
      </div>
    );
  }

  const planName = planData?.plan_name || (isUpgradePagarme ? 'Upgrade de plano' : 'Sua assinatura');
  const priceLabel = formatBRL(planData?.plan_price);
  const diffLabel = formatBRL(planData?.diff_amount);
  const primaryCta = isUpgradePagarme
    ? (diffLabel ? `Pagar ${diffLabel}` : 'Pagar diferença')
    : (priceLabel ? `Assinar por ${priceLabel}/mês` : 'Confirmar assinatura');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-white py-8 sm:py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Cabeçalho */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/5 border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500 mb-3">
            <Lock className="w-3.5 h-3.5" /> Checkout seguro
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{isUpgradePagarme ? (planData?.vehicle_change ? 'Confirme a troca de veículo' : 'Confirme seu upgrade') : 'Finalize sua assinatura'}</h1>
          <p className="mt-2 text-sm text-slate-500">{isUpgradePagarme ? 'Pague a diferença para concluir a alteração.' : 'Falta pouco para você aproveitar todos os benefícios da JATOO.'}</p>
        </div>

        <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6 lg:gap-8 items-start">
          {/* COLUNA ESQUERDA — Resumo, Segurança e Bandeiras */}
          <div className="space-y-5 lg:sticky lg:top-8">
            {/* 1. Resumo do plano */}
            <div data-testid="plan-summary" className="rounded-3xl bg-slate-900 text-white p-6 sm:p-8 shadow-2xl shadow-slate-900/20 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/20 blur-3xl" aria-hidden />
              <div className="relative">
                <div className="flex items-center gap-2 text-slate-300 text-[11px] font-semibold uppercase tracking-[0.15em] mb-4">
                  <Sparkles className="w-4 h-4" /> {isUpgradePagarme ? 'Resumo do upgrade' : 'Resumo da assinatura'}
                </div>
                <h3 className="text-2xl font-bold" data-testid="plan-name">{planName}</h3>
                {isUpgradePagarme ? (
                  <div className="mt-3" data-testid="plan-price">
                    <p className="text-slate-400 text-[11px] uppercase tracking-wider mb-1">Diferença a pagar agora</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-5xl font-extrabold tracking-tight">{diffLabel}</span>
                    </div>
                    {priceLabel && (
                      <p className="mt-2 text-sm text-slate-400">Nova mensalidade: <span className="text-slate-200 font-semibold">{priceLabel}/mês</span></p>
                    )}
                    {planData?.vehicle_change && planData?.new_vehicle_label && (
                      <p className="mt-1 text-sm text-slate-400" data-testid="vehicle-change-summary">
                        Veículo: <span className="text-slate-300">{planData?.current_vehicle_label}</span> → <span className="text-slate-200 font-semibold">{planData?.new_vehicle_label}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  priceLabel && (
                    <div className="mt-3 flex items-baseline gap-1.5" data-testid="plan-price">
                      <span className="text-5xl font-extrabold tracking-tight">{priceLabel}</span>
                      <span className="text-slate-400 text-base font-medium">/mês</span>
                    </div>
                  )
                )}
                <div className="mt-6 space-y-3">
                  {['Agendamento pelo aplicativo', 'Suporte prioritário', 'Cancele quando quiser'].map((b) => (
                    <div key={b} className="flex items-center gap-3 text-sm text-slate-100">
                      <span className="flex-none w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      {b}
                    </div>
                  ))}
                </div>
                <div className="mt-7 pt-5 border-t border-white/10 space-y-1.5 text-xs text-slate-400">
                  {isUpgradePagarme ? (
                    <>
                      <p>Cobrança única da diferença agora (sem proração).</p>
                      <p>Sua mensalidade passa a ser {priceLabel} a partir da próxima renovação.</p>
                    </>
                  ) : (
                    <>
                      <p>Cobrança mensal recorrente • Próxima cobrança 30 dias após a confirmação.</p>
                      <p>Você pode cancelar sua assinatura conforme as regras do plano.</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 2. Pagamento seguro */}
            <div data-testid="security-info" className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm">
              <div className="flex items-start gap-3.5">
                <span className="flex-none w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Pagamento 100% seguro</p>
                  <ul className="mt-2 space-y-1.5 text-xs text-slate-500">
                    <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Processado pela Pagar.me</li>
                    <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Seus dados são criptografados</li>
                    <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> A JATOO não armazena os dados do seu cartão</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 3. Bandeiras aceitas */}
            <div data-testid="accepted-brands" className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-2.5">Bandeiras aceitas</p>
              <div className="flex flex-wrap gap-2">
                {ACCEPTED_BRANDS.map((b) => (
                  <span key={b} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-600 tracking-wide">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* COLUNA DIREITA — Formulário do cartão */}
          <Card className="rounded-3xl border-slate-200/80 shadow-2xl shadow-slate-900/5 p-6 sm:p-8" data-testid="payment-card-form">
            <div className="flex items-center gap-2.5 mb-1">
              <span className="flex-none w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </span>
              <h2 className="text-lg font-bold text-slate-900">Dados do cartão</h2>
            </div>
            <p className="text-xs text-slate-400 mb-6 pl-11 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Enviados com segurança direto ao Pagar.me
            </p>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="card-number" className="text-slate-700 text-sm font-medium">Número do cartão</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="card-number"
                    data-testid="card-number-input"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    className="h-12 rounded-xl text-base tracking-wide pr-24"
                    value={card.number}
                    onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                  />
                  {brand && (
                    <span
                      data-testid="card-brand-label"
                      className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded-md bg-primary/10 text-xs font-bold text-primary"
                    >
                      {brand}
                    </span>
                  )}
                </div>
                {card.number && !luhnValid(onlyDigits(card.number)) && (
                  <p className="text-xs text-red-500 mt-1.5" data-testid="card-number-error">Número inválido</p>
                )}
              </div>
              <div>
                <Label htmlFor="card-holder" className="text-slate-700 text-sm font-medium">Nome impresso no cartão</Label>
                <Input
                  id="card-holder"
                  data-testid="card-holder-input"
                  autoComplete="cc-name"
                  placeholder="COMO ESTÁ NO CARTÃO"
                  className="h-12 rounded-xl mt-1.5 uppercase tracking-wide"
                  value={card.holder_name}
                  onChange={(e) => setCard({ ...card, holder_name: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="card-exp" className="text-slate-700 text-sm font-medium">Validade (MM/AA)</Label>
                  <Input
                    id="card-exp"
                    data-testid="card-exp-input"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="MM/AA"
                    className="h-12 rounded-xl mt-1.5 text-base"
                    value={card.exp}
                    onChange={(e) => setCard({ ...card, exp: formatExp(e.target.value) })}
                  />
                  {card.exp.length === 5 && !expValid(card.exp) && (
                    <p className="text-xs text-red-500 mt-1.5" data-testid="card-exp-error">Validade inválida</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="card-cvv" className="text-slate-700 text-sm font-medium">CVV</Label>
                  <Input
                    id="card-cvv"
                    data-testid="card-cvv-input"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="123"
                    className="h-12 rounded-xl mt-1.5 text-base"
                    value={card.cvv}
                    onChange={(e) => setCard({ ...card, cvv: onlyDigits(e.target.value).slice(0, 4) })}
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold rounded-xl shadow-lg shadow-primary/25 transition-all duration-200 hover:shadow-primary/40 hover:-translate-y-0.5 disabled:opacity-60 disabled:shadow-none disabled:hover:translate-y-0"
                disabled={submitting || !formValid}
                data-testid="pay-submit-button"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando...</>
                ) : (
                  primaryCta
                )}
              </Button>
              <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Ambiente criptografado • seus dados do cartão não passam pela JATOO
              </p>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
